import useExamAttendanceSession from '../attendance/useExamAttendanceSession';
import { evaluateExamAnswers } from '../../services/examEvaluation';
import { attemptEndTime, flattenExamAnswers, flattenExamQuestions, isSubmittedAttempt, selectExamAttempt, startAttemptOnce, withExamTimeout } from './examAttemptLifecycle';
import React, { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    doc, 
    getDoc, 
    addDoc, 
    collection, 
    query, 
    where, 
    getDocs, 
    serverTimestamp, 
    onSnapshot,
    runTransaction
} from '../../lib/auditedFirestore';

// --- Shared Configuration Imports ---
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';

// --- UI Components ---
import ExamTimer from '../../components/ExamTimer'; 
import Spinner from '../../components/Spinner'; 
import Skeleton from '../../components/Skeleton'; 
import AdvancedWordProcessor from '../../components/editors/AdvancedWordProcessor';
import ExamSpreadsheetResponse from './ExamSpreadsheetResponse';
import {flushEditorChanges} from '../../components/editors/useEditorFrame';
import {hasDescriptiveResponse,hasStarterSpreadsheet} from '../../utils/starterSpreadsheet';

// --- Styles for Animations (Injected) ---
const styles = `
    @keyframes slideUpFade {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
    .animate-slide-up {
        animation: slideUpFade 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }
    .btn-press:active {
        transform: scale(0.95);
    }
    .pulse-red {
        animation: pulse-red 2s infinite;
    }
    @keyframes pulse-red {
        0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
        70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
        100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
    }
    /* Fixed selection stability */
    .stable-select {
        user-select: text !important;
        -webkit-user-select: text !important;
    }
    /* Instil Brand Gradient Text */
    .text-instil-gradient {
        background: linear-gradient(90deg, #D02C70 0%, #F05530 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }
    /* Custom Scrollbar for Navigator */
    .custom-scrollbar::-webkit-scrollbar { width: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #f9fafb; border-radius: 4px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
`;

// --- Helper Functions ---
// A late autosave from another tab must never replace a sealed/submitted answer.
async function writeOpenAttempt(attemptId, data, mode = 'finish') {
    return runTransaction(db, async transaction => {
        const ref = doc(db, 'attempts', attemptId);
        const snapshot = await transaction.get(ref);
        if (!snapshot.exists()) throw new Error('Your exam attempt is unavailable.');
        const current = snapshot.data();
        if (isSubmittedAttempt(current) || (mode === 'autosave' && current.submissionState === 'marking')) return false;
        if (mode === 'seal' && current.submissionState === 'marking') {
            return { answers: current.answers || {}, flaggedQuestions: current.flaggedQuestions || {} };
        }
        transaction.update(ref, data);
        return mode === 'seal' ? { answers: data.answers, flaggedQuestions: data.flaggedQuestions } : true;
    });
}

// --- Toast Component ---
const ToastContainer = ({ toasts, removeToast }) => (
    <div className="fixed top-5 right-5 z-[100] flex flex-col gap-3 pointer-events-none">
        {toasts.map(toast => (
            <div 
                key={toast.id} 
                className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg shadow-xl text-white transform transition-all duration-300 ease-out animate-slide-up ${toast.type === 'error' ? 'bg-red-500' : 'bg-gradient-to-r from-[#D02C70] to-[#F05530]'}`}
                style={{ minWidth: '300px' }}
            >
                <span className="text-xl">{toast.type === 'error' ? '⚠️' : '✅'}</span>
                <div className="flex-1">
                    <p className="font-bold text-sm">{toast.title}</p>
                    <p className="text-xs opacity-90">{toast.message}</p>
                </div>
                <button type="button" onClick={() => removeToast(toast.id)} className="opacity-70 hover:opacity-100">&times;</button>
            </div>
        ))}
    </div>
);

// --- MEMOIZED QUESTION DISPLAY COMPONENT ---
const QuestionDisplay = memo(({ 
    question, 
    index, 
    sectionInfo, 
    isFirstOfSection, 
    view, 
    answer, 
    onDescriptiveChange, 
    onViewChange,
    scenarioRef,
    questionTextRef
}) => {

    useEffect(() => {
        if (scenarioRef?.current && question.scenarioText) {
            if (scenarioRef.current.innerHTML !== question.scenarioText) {
                scenarioRef.current.innerHTML = question.scenarioText;
            }
        }
    }, [question.scenarioText, scenarioRef]);

    useEffect(() => {
        if (questionTextRef?.current && question.text) {
            if (questionTextRef.current.innerHTML !== question.text) {
                questionTextRef.current.innerHTML = question.text;
            }
        }
    }, [question.text, questionTextRef]);

    return (
        <div key={question.id} className="animate-slide-up">
            
            {/* Section Header */}
            {isFirstOfSection && sectionInfo && (
                <div className="bg-white border-l-4 border-[#D02C70] p-4 rounded-r-lg mb-6 shadow-sm">
                    <h2 className="text-xl font-bold text-gray-800">{sectionInfo.name}</h2>
                    <p className="text-sm text-gray-500 mt-1">{sectionInfo.title} • {sectionInfo.numQuestions} questions</p>
                </div>
            )}
            
            {/* CASE STUDY SCENARIO RENDER */}
            {question.isCaseStudyChild && (
                <div className="mb-6 p-5 bg-white border border-gray-200 rounded-xl shadow-sm relative group hover:border-[#D02C70]/30 transition-colors">
                    <div className="absolute top-0 right-0 bg-[#FFF0F5] text-[#D02C70] text-[10px] font-bold px-2 py-1 rounded-bl-lg rounded-tr-xl uppercase tracking-wider">Case Study</div>
                    {/* Scenario Text */}
                    {question.scenarioText && (
                        <div 
                            ref={scenarioRef}
                            className="prose prose-sm max-w-none mb-4 select-text cursor-text text-gray-700 stable-select"
                            style={{ userSelect: 'text' }}
                            onMouseDown={(e) => e.stopPropagation()} 
                        />
                    )}
                    {/* Scenario Images */}
                    {question.scenarioImages?.length > 0 && (
                        <div className="grid grid-cols-2 gap-3">
                            {question.scenarioImages.map((src, idx) => (
                                <img key={idx} src={src} alt="Scenario" className="max-w-full h-auto rounded-lg border" />
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-800">
                    Question {index + 1}
                </h2>
                <span className="text-xs font-bold text-[#D02C70] bg-[#FFF0F5] px-3 py-1 rounded-full border border-[#D02C70]/20">{question.marks} Marks</span>
            </div>
            
            <div className="space-y-5">
                {/* Standard Question Images */}
                {Array.isArray(question.images) && question.images.map((src, idx) => (
                    <img key={idx} src={src} alt={`Question ${idx + 1}`} className="max-w-full h-auto rounded-lg border shadow-sm" />
                ))}
                
                {/* Standard Question Text */}
                {question.text && (
                     <div 
                        ref={questionTextRef}
                        className="prose prose-lg max-w-none text-gray-800 select-text cursor-text leading-relaxed stable-select" 
                        style={{ userSelect: 'text' }}
                        onMouseDown={(e) => e.stopPropagation()} 
                     />
                )}
            </div>

            {/* Format Toggle for Descriptive */}
            {question.type === 'descriptive' && (
                <div className="mt-8 pt-4 border-t border-dashed border-gray-300">
                    <h3 className="text-sm font-bold text-gray-500 uppercase mb-3 tracking-wide">Response Format</h3>
                    <div className="flex gap-3 bg-gray-100 p-1 rounded-lg w-fit">
                        <button 
                            type="button" 
                            onClick={(e) => { e.preventDefault(); onViewChange('word'); }} 
                            className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${view === 'word' ? 'bg-white text-[#D02C70] shadow-sm ring-1 ring-[#D02C70]/20' : 'text-gray-600 hover:text-gray-800'}`}
                        >
                            Word Processor
                        </button>
                        <button 
                            type="button" 
                            onClick={(e) => { e.preventDefault(); onViewChange('excel'); }} 
                            className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${view === 'excel' ? 'bg-white text-[#D02C70] shadow-sm ring-1 ring-[#D02C70]/20' : 'text-gray-600 hover:text-gray-800'}`}
                        >
                            Spreadsheet
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
});

const ExamTaker = () => {
    // 1. Hooks & Routing
    const { examId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    // 2. State Management
    const [rawExam, setRawExam] = useState(null); 
    const [flatQuestions, setFlatQuestions] = useState([]); 
    
    // --- Access Control State ---
    const [isMaintenance, setIsMaintenance] = useState(false);
    const [isAttemptBlocked, setIsAttemptBlocked] = useState(false); 
    const [accessError, setAccessError] = useState(null);

    const [loadingExam, setLoadingExam] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    
    const [answers, setAnswers] = useState({});
    const answersRef=useRef(answers);answersRef.current=answers;
    const [flaggedQuestions, setFlaggedQuestions] = useState({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    
    // Target end time
    const [targetEndTime, setTargetEndTime] = useState(null); 
    
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [showNavigator, setShowNavigator] = useState(false);
    const [attemptId, setAttemptId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showCalculator, setShowCalculator] = useState(false);
    const [showFormulaSheet, setShowFormulaSheet] = useState(false);
    const [paperData, setPaperData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submissionInFlight = useRef(false);
    const attendance=useExamAttendanceSession(examId,isSubmitting);
    const beginAttendance=attendance.begin;
    const clockOffset = useRef(0); clockOffset.current = attendance.clockOffset || 0;
    const studentId = user?.uid;
    const [submissionNotice, setSubmissionNotice] = useState(null);
    const [submissionProgress, setSubmissionProgress] = useState('Saving your answers…');
    const submissionSnapshot = useRef(null);
    const savedEvaluation = useRef(null);
    const saveChain = useRef(Promise.resolve());
    const autosaveTimer = useRef(null);
    const expiryHandled = useRef(false);
    const attemptFinished = useRef(false);
    const initialization = useRef(null);
    const flagsRef = useRef(flaggedQuestions);
    flagsRef.current = flaggedQuestions;
    const contextKey = `${studentId || ''}:${examId}`;
    const activeContext = useRef({ key: contextKey });
    if (activeContext.current.key !== contextKey) activeContext.current = { key: contextKey };
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const attendancePhase = attendance.session?.phase;
    const [questionPanelWidth, setQuestionPanelWidth] = useState(50);
    
    // --- Text Selection & Highlighting State ---
    const [selectionToolbar, setSelectionToolbar] = useState(null); 
    const [isHighlighterActive, setIsHighlighterActive] = useState(false); 
    const [highlightColor, setHighlightColor] = useState('#fef08a'); 

    // --- Toast State ---
    const [toasts, setToasts] = useState([]);

    // 3. Refs
    const draggedItem = useRef(null);
    const mainPanelRef = useRef(null);
    const dividerRef = useRef(null);
    const scenarioRef = useRef(null);
    const questionTextRef = useRef(null);
    const isMouseDown = useRef(false);

    // --- Toast Helper ---
    const showToast = useCallback((title, message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, title, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
    }, []);

    const removeToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

    // --- Helper: Structure Answers for DB (Flat -> Nested) ---
    const structureAnswersForSave = useCallback((flatAnswers) => {
        const structured = {};
        Object.keys(flatAnswers).forEach(key => {
            if (key.includes('-')) {
                const [parentIdx, subIdx] = key.split('-');
                if (!structured[parentIdx]) structured[parentIdx] = {};
                structured[parentIdx][subIdx] = flatAnswers[key];
            } else {
                structured[key] = flatAnswers[key];
            }
        });
        return structured;
    }, []);

    // --- EFFECT 0: Maintenance Check ---
    useEffect(() => {
        const systemRef = doc(db, 'system', 'config');
        const unsubscribe = onSnapshot(systemRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.maintenanceMode === true) {
                    setIsMaintenance(true);
                } else {
                    setIsMaintenance(false);
                }
            }
        });
        return () => unsubscribe();
    }, []);

    // --- EFFECT 1: Fetch Exam Data & Flatten ---
    useEffect(() => {
        let isMounted = true;
        setLoadingExam(true); setIsLoaded(false); setRawExam(null); setFlatQuestions([]);
        setAnswers({}); answersRef.current = {}; setFlaggedQuestions({}); flagsRef.current = {};
        setAttemptId(null); setTargetEndTime(null); setCurrentQuestionIndex(0); setPaperData(null);
        setAccessError(null); setIsAttemptBlocked(false); setHasUnsavedChanges(false);
        setIsSubmitting(false); setIsSaving(false); setSubmissionNotice(null);
        submissionInFlight.current = false; submissionSnapshot.current = null;
        savedEvaluation.current = null; expiryHandled.current = false; attemptFinished.current = false;
        initialization.current = null; saveChain.current = Promise.resolve();
        clearTimeout(autosaveTimer.current);

        if (!examId) {
            console.error("No Exam ID provided");
            setLoadingExam(false);
            return;
        }

        const fetchExamData = async () => {
            try {
                const docRef = doc(db, 'exams', examId);
                const snap = await withExamTimeout(getDoc(docRef));
                
                if (snap.exists()) {
                    const data = { id: snap.id, ...snap.data() };
                    
                    if (data.isHidden) {
                        if (isMounted) setAccessError('hidden');
                        if (isMounted) setLoadingExam(false);
                        return;
                    }

                    if (!data.activeVersionId || !data.publishedQuestionsUrl) {
                        if (isMounted) setAccessError('unpublished');
                        if (isMounted) setLoadingExam(false);
                        return;
                    }

                    data.title = data.publishedTitle || data.title;
                    data.duration = data.publishedDuration || data.duration;
                    data.mode = data.publishedMode || data.mode;
                    data.questionsUrl = data.publishedQuestionsUrl;
                    
                    let loadedQuestions = [];

                    if (data.questionsUrl) {
                        try {
                            const res = await withExamTimeout(fetch(data.questionsUrl));
                            if (!res.ok) throw new Error('Failed to fetch questions file');
                            loadedQuestions = await withExamTimeout(res.json());
                        } catch (error) {
                            console.error("Failed to fetch questions JSON from Storage:", error);
                            showToast("Error", "Failed to load exam questions from cloud storage.", "error");
                        }
                    } 
                    
                    if ((!loadedQuestions || loadedQuestions.length === 0) && data.questions && Array.isArray(data.questions)) {
                        loadedQuestions = data.questions;
                    }

                    data.questions = loadedQuestions;
                    if (!Array.isArray(loadedQuestions) || !loadedQuestions.length) throw new Error('No published questions could be loaded.');
                    const flattened = flattenExamQuestions(loadedQuestions);
                    if (isMounted) setRawExam(data);
                    if (flattened.length === 0) console.warn("Exam has no valid questions.");
                    if (isMounted) setFlatQuestions(flattened);

                } else {
                    showToast("Error", "Exam not found", "error");
                    navigate('/dashboard');
                }
            } catch (error) {
                console.error("Error fetching exam:", error);
                if (isMounted) setAccessError("load-failed");
            } finally {
                if (isMounted) setLoadingExam(false);
            }
        };

        fetchExamData();
        return () => { isMounted = false; };
    }, [examId, studentId, navigate, showToast]);

    // --- Derived State: Sections (Memoized) ---
    const sections = React.useMemo(() => {
        if (!flatQuestions.length) return [];
        if (!paperData?.sections || paperData.sections.length === 0) {
            return [{ name: "All Questions", title: "", questions: flatQuestions }];
        }

        const grouped = paperData.sections.map(sec => ({...sec, questions: []}));
        const misc = { name: "Misc", title: "", questions: [] }; 

        flatQuestions.forEach((q) => {
            const section = grouped.find(s => s.name === q.section);
            if (section) {
                section.questions.push(q);
            } else {
                misc.questions.push(q);
            }
        });
        
        if (misc.questions.length > 0) grouped.push(misc);
        return grouped.filter(s => s.questions.length > 0);
    }, [flatQuestions, paperData]);

    const isFirstQuestionOfSection = React.useMemo(() => {
        if (!flatQuestions.length) return false;
        if (!flatQuestions[currentQuestionIndex]) return false; 

        const currentQ = flatQuestions[currentQuestionIndex];
        const section = sections.find(s => s.name === currentQ.section);
        if (!section) return false;
        return section.questions[0].id === currentQ.id;
    }, [currentQuestionIndex, sections, flatQuestions]);

    const currentSectionInfo = useMemo(() => {
        if (!flatQuestions[currentQuestionIndex] || !paperData?.sections) return null;
        const currentQ = flatQuestions[currentQuestionIndex];
        return paperData.sections.find(sec => sec.name === currentQ.section);
    }, [flatQuestions, currentQuestionIndex, paperData]);

    // --- Initialize once per student/exam. Retakes start with entirely new answers. ---
    useEffect(() => {
        let isMounted = true;
        if (!rawExam || rawExam.id !== examId || !studentId) return;
        const context = activeContext.current;
        const loadAttempt = async () => {
            const managedSession = await withExamTimeout(beginAttendance());
            const attemptsRef = collection(db, 'attempts');
            let attempt;
            if (managedSession) {
                const snap = await withExamTimeout(getDoc(doc(db, 'attempts', managedSession.attemptId)));
                if (!snap.exists()) throw new Error('Your exam session could not be found. Please contact your coordinator.');
                attempt = { ...snap.data(), id: managedSession.attemptId };
            } else {
                const snapshot = await withExamTimeout(getDocs(query(attemptsRef, where('userId', '==', studentId), where('examId', '==', rawExam.id))));
                const selection = selectExamAttempt(snapshot.docs.map(d => ({ ...d.data(), id: d.id })));
                if (selection.kind === 'blocked') return { blocked: true };
                if (selection.kind === 'resume') attempt = selection.attempt;
                else {
                    const fresh = { examId: rawExam.id, userId: studentId, answers: {}, flaggedQuestions: {}, evaluations: {},
                        startedAt: serverTimestamp(), submittedAt: null, questionsUrl: rawExam.questionsUrl,
                        examVersionId: rawExam.activeVersionId, durationMinutes: Number(rawExam.duration) };
                    let id;
                    if (selection.kind === 'retake') {
                        // Consume permission and create the new attempt together. Two tabs cannot take the same retake.
                        const previousRef = doc(db, 'attempts', selection.previous.id);
                        const freshRef = doc(attemptsRef);
                        id = await withExamTimeout(runTransaction(db, async transaction => {
                            const previous = await transaction.get(previousRef);
                            const data = previous.data();
                            if (data?.allowRetake !== true) {
                                if (data?.retakeAttemptId) return data.retakeAttemptId;
                                throw new Error('This retake permission has already been used. Please reopen the exam.');
                            }
                            transaction.set(freshRef, { ...fresh, retakeOf: selection.previous.id });
                            transaction.update(previousRef, { allowRetake: false, retaken: true, retakeAttemptId: freshRef.id });
                            return freshRef.id;
                        }));
                    } else {
                        const created = await withExamTimeout(addDoc(attemptsRef, fresh));
                        id = created.id;
                    }
                    attempt = { ...fresh, id, answers: {}, flaggedQuestions: {} };
                }
                const latest = await withExamTimeout(getDoc(doc(db, 'attempts', attempt.id)));
                if (!latest.exists()) throw new Error('Your exam attempt could not be loaded.');
                attempt = { ...attempt, ...latest.data(), id: attempt.id };
            }
            if (isSubmittedAttempt(attempt) || managedSession?.phase === 'completed') return { blocked: true };
            let questions = rawExam.questions;
            const questionsUrl = attempt.questionsUrl || rawExam.questionsUrl;
            if (questionsUrl !== rawExam.questionsUrl) {
                const response = await withExamTimeout(fetch(questionsUrl));
                if (!response.ok) throw new Error('Your saved exam questions could not be loaded.');
                questions = await withExamTimeout(response.json());
                if (!Array.isArray(questions) || !questions.length) throw new Error('Your saved exam questions are unavailable.');
            }
            const endTime = attemptEndTime(attempt, rawExam.duration, managedSession) - clockOffset.current;
            return { attempt, managedSession, endTime, questions, questionsUrl };
        };
        // Keep the resolved operation across provider renders and StrictMode effect replay.
        if (!initialization.current || initialization.current.context !== context) {
            initialization.current = { context, promise: startAttemptOnce(context.key, loadAttempt) };
        }
        const finish = async () => {
            try {
                const result = await initialization.current.promise;
                if (!isMounted || activeContext.current !== context) return;
                if (result.blocked) { setIsAttemptBlocked(true); return; }
                const { attempt, managedSession, endTime, questions, questionsUrl } = result;
                const restored = flattenExamAnswers(attempt.answers || {}, questions);
                answersRef.current = restored; setAnswers(restored);
                flagsRef.current = attempt.flaggedQuestions || {}; setFlaggedQuestions(flagsRef.current);
                setAttemptId(attempt.id); setTargetEndTime(endTime); setFlatQuestions(flattenExamQuestions(questions));
                if (questionsUrl !== rawExam.questionsUrl) setRawExam(previous => ({ ...previous, questions, questionsUrl }));
                const wasSubmitting = managedSession?.phase === 'sealed' || attempt.submissionState === 'marking';
                if (wasSubmitting || endTime <= Date.now()) {
                    expiryHandled.current = true;
                    submissionSnapshot.current = { answers: attempt.answers || {}, flaggedQuestions: attempt.flaggedQuestions || {} };
                    setSubmissionNotice(wasSubmitting ? 'unfinished' : 'expired');
                }
                setIsLoaded(true);
                if (rawExam.paperId) {
                    const paper = await withExamTimeout(getDoc(doc(db, 'papers', rawExam.paperId)));
                    if (isMounted && paper.exists()) setPaperData({ ...paper.data(), id: paper.id });
                }
            } catch (error) {
                if (isMounted) { console.error('Exam start failed:', error); setAccessError('session-failed'); }
            }
        };
        finish();
        return () => { isMounted = false; };
    }, [rawExam, examId, studentId, beginAttendance]);

    // Autosaves are serialized and drained before sealing the final answer snapshot.
    useEffect(() => {
        if (!isLoaded || !attemptId || !hasUnsavedChanges || isSubmitting || submissionNotice ||
            (attendancePhase && attendancePhase !== 'open')) return;
        const context = activeContext.current;
        const snapshot = answers;
        const flags = flaggedQuestions;
        setIsSaving(true);
        autosaveTimer.current = setTimeout(() => {
            if (submissionInFlight.current || activeContext.current !== context) return;
            const task = saveChain.current.catch(() => {}).then(() => {
                if (activeContext.current !== context || submissionInFlight.current) return false;
                return writeOpenAttempt(attemptId, { answers: structureAnswersForSave(snapshot), flaggedQuestions: flags }, 'autosave');
            });
            saveChain.current = task;
            withExamTimeout(task).then(saved => {
                if (saved !== false && activeContext.current === context && answersRef.current === snapshot && flagsRef.current === flags) setHasUnsavedChanges(false);
            }).catch(() => {
                if (activeContext.current === context) showToast('Autosave paused', 'Keep this exam open and check your connection. Your unsaved answers are still on this page.', 'error');
            }).finally(() => { if (activeContext.current === context) setIsSaving(false); });
        }, 2000);
        return () => clearTimeout(autosaveTimer.current);
    }, [answers, flaggedQuestions, attemptId, isLoaded, hasUnsavedChanges, isSubmitting, submissionNotice, structureAnswersForSave, attendancePhase, showToast]);

    useEffect(() => {
        if (!isLoaded || isSubmitting || submissionInFlight.current || !attendancePhase) return;
        if (attendancePhase === 'completed') {
            expiryHandled.current = true; attemptFinished.current = true;
            navigate('/dashboard', { replace: true });
        } else if (attendancePhase === 'sealed' && !submissionSnapshot.current) {
            expiryHandled.current = true;
            // Reopen to recover the server's sealed answers, not this tab's potentially stale draft.
            setAccessError('sealed-elsewhere');
        }
    }, [attendancePhase, isLoaded, isSubmitting, navigate]);

    // --- BeforeUnload Alert for Unsaved Changes ---
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    // Keep a single immutable snapshot across failures/retries. Marking failures cannot restart the timer.
    const handleSubmit = useCallback(async () => {
        if (submissionInFlight.current || attemptFinished.current) return;
        if (!studentId || !attemptId || !isLoaded) return;
        flushEditorChanges();
        clearTimeout(autosaveTimer.current);
        if (!submissionSnapshot.current) submissionSnapshot.current = {
            answers: structureAnswersForSave(answersRef.current), flaggedQuestions: { ...flagsRef.current }
        };
        let snapshot = submissionSnapshot.current;
        const context = activeContext.current;
        const current = () => mounted.current && activeContext.current === context;
        submissionInFlight.current = true;
        expiryHandled.current = true;
        setIsSubmitting(true); setSubmissionNotice(null); setSubmissionProgress('Saving your answers…');
        try {
            await withExamTimeout(saveChain.current.catch(() => {}));
            if (!current()) return;
            if (attendance.session) {
                await withExamTimeout(attendance.seal(snapshot.answers, snapshot.flaggedQuestions));
                // Another tab may have sealed first. Grade the server's frozen answers.
                const frozen = await withExamTimeout(getDoc(doc(db, 'attempts', attemptId)));
                if (!frozen.exists()) throw new Error('The submitted answer snapshot could not be loaded.');
                const data = frozen.data();
                if (!current()) return;
                if (isSubmittedAttempt(data)) {
                    attemptFinished.current = true; setHasUnsavedChanges(false);
                    navigate('/dashboard', { replace: true }); return;
                }
                const sealed = { answers: data.answers || {}, flaggedQuestions: data.flaggedQuestions || {} };
                if (JSON.stringify(sealed) !== JSON.stringify(snapshot)) savedEvaluation.current = null;
                snapshot = sealed; submissionSnapshot.current = sealed;
            } else {
                // A server-confirmed checkpoint lets a refresh safely resume submission.
                const open = await withExamTimeout(writeOpenAttempt(attemptId, {
                    ...snapshot, submissionState: 'marking', questionsUrl: rawExam.questionsUrl
                }, 'seal'));
                if (!open) {
                    if (current()) { attemptFinished.current = true; setHasUnsavedChanges(false); navigate(`/exam/${rawExam.id}/result/${attemptId}`, { replace: true }); }
                    return;
                }
                if (!current()) return;
                if (JSON.stringify(open) !== JSON.stringify(snapshot)) savedEvaluation.current = null;
                snapshot = open;
                submissionSnapshot.current = open;
            }
            if (!current()) return;
            if (!savedEvaluation.current) {
                if (current()) setSubmissionProgress('Checking your answers…');
                const marked = await evaluateExamAnswers({
                    questions: rawExam.questions, answers: snapshot.answers, examId: rawExam.id,
                    onProgress: ({ completed, total }) => {
                        if (current()) setSubmissionProgress(total ? `Marking written answers: ${completed} of ${total}` : 'Checking your answers…');
                    }
                });
                if (!current()) return;
                savedEvaluation.current = marked;
            }
            const result = savedEvaluation.current;
            const finalAttemptData = {
                questionsUrl: rawExam.questionsUrl, ...snapshot,
                evaluations: result.evaluations, score: result.score,
                autoGradedTotalMarks: result.autoGradedTotalMarks, aiGradedScore: result.aiGradedScore,
                descriptiveTotalMarks: result.descriptiveTotalMarks, totalQuestions: flatQuestions.length,
                evaluationStatus: result.pendingCount ? 'pending' : 'complete',
                submissionState: 'submitted', submittedAt: serverTimestamp()
            };
            if (current()) setSubmissionProgress('Finishing your submission…');
            const completed = attendance.session
                ? await withExamTimeout(attendance.complete(finalAttemptData), 45000)
                : await withExamTimeout(writeOpenAttempt(attemptId, finalAttemptData));
            if (!current()) return;
            attemptFinished.current = true;
            setHasUnsavedChanges(false);
            navigate(completed?.blocked ? '/dashboard' : `/exam/${rawExam.id}/result/${attemptId}`, { replace: true });
        } catch (error) {
            if (current()) {
                console.error('Exam submission paused:', error);
                setHasUnsavedChanges(true); setSubmissionNotice('failed');
            }
        } finally {
            if (current()) { submissionInFlight.current = false; setIsSubmitting(false); setIsSaving(false); }
        }
    }, [studentId, attemptId, isLoaded, rawExam, flatQuestions.length, navigate, structureAnswersForSave, attendance]);
    const handleTimeUp = useCallback(() => {
        if (expiryHandled.current || attemptFinished.current || submissionInFlight.current) return;
        expiryHandled.current = true;
        return handleSubmit();
    }, [handleSubmit]);

    // --- UI Handlers (Memoized) ---
    const getCurrentAnswer = useCallback(() => {
        if (!flatQuestions || flatQuestions.length === 0 || !flatQuestions[currentQuestionIndex]) return undefined;
        const qId = flatQuestions[currentQuestionIndex].id;
        return answers[qId];
    }, [flatQuestions, currentQuestionIndex, answers]);

    const updateAnswer = useCallback((val) => {
        const qId = flatQuestions[currentQuestionIndex]?.id;
        if (!qId || submissionSnapshot.current) return;
        const next = { ...answersRef.current, [qId]: val };
        answersRef.current = next; setAnswers(next);
        setHasUnsavedChanges(true); 
    }, [flatQuestions, currentQuestionIndex]);

    const handleMcqAnswerChange = (oIndex) => {
        const current = getCurrentAnswer() || [];
        if (current.includes(oIndex)) updateAnswer(current.filter(i => i !== oIndex));
        else updateAnswer([...current, oIndex]);
    };

    const handleShortAnswerChange = (value) => {
        updateAnswer(value);
    };

    const handleMatchingDrop = (promptIndex, optionIndex) => {
        const current = { ...(getCurrentAnswer() || {}) }; 
        Object.keys(current).forEach(pIdx => {
            if (current[pIdx] === optionIndex) delete current[pIdx]; 
        });
        updateAnswer({ ...current, [promptIndex]: optionIndex });
    };
    
    // Update the ref synchronously so timed submission includes a just-flushed cell.
    const handleDescriptiveAnswerChange = useCallback((answerPart, value) => {
        const question=flatQuestions[currentQuestionIndex],qId=question?.id;if(!qId || submissionSnapshot.current)return;
        let current=answersRef.current[qId];
        if(typeof current==='string')current={type:'descriptive',wordContent:current,view:'word'};
        else if(!current)current={type:'descriptive',view:hasStarterSpreadsheet(question)?'excel':'word'};
        const next={...answersRef.current,[qId]:{...current,[answerPart]:value}};
        answersRef.current=next;setAnswers(next);setHasUnsavedChanges(true);
    },[flatQuestions,currentQuestionIndex]);

    const setDescriptiveAnswerView = useCallback((view) => {
        const qId = flatQuestions[currentQuestionIndex]?.id;
        if (!qId) return;

        if (submissionSnapshot.current) return;
        let current = answersRef.current[qId];
        if (typeof current === 'string') current = { type: 'descriptive', wordContent: current };
        else if (!current) current = { type: 'descriptive' };
        const next = { ...answersRef.current, [qId]: { ...current, view } };
        answersRef.current = next; setAnswers(next);
        setHasUnsavedChanges(true); 
    }, [flatQuestions, currentQuestionIndex]);

    const toggleFlagQuestion = () => {
        const qId = flatQuestions[currentQuestionIndex]?.id;
        if (!qId || submissionSnapshot.current) return;
        const next = { ...flagsRef.current };
        if (next[qId]) delete next[qId]; else next[qId] = true;
        flagsRef.current = next; setFlaggedQuestions(next); setHasUnsavedChanges(true);
    };

    // --- Highlight Helper ---
    const applyHighlight = useCallback((range, colorToUse = '#fef08a') => {
        if (!range) return;
        try {
            const span = document.createElement('span');
            span.style.backgroundColor = colorToUse;
            span.style.color = 'black'; 
            span.className = 'highlight-manual'; 
            range.surroundContents(span);

            const currentQ = flatQuestions[currentQuestionIndex];
            let updatedQuestions = [...flatQuestions];
            
            if (scenarioRef.current && scenarioRef.current.contains(range.commonAncestorContainer)) {
                const newScenarioText = scenarioRef.current.innerHTML;
                updatedQuestions = updatedQuestions.map(q => 
                    (q.isCaseStudyChild && q.originalIndex === currentQ.originalIndex)
                        ? { ...q, scenarioText: newScenarioText }
                        : q
                );
            } else if (questionTextRef.current && questionTextRef.current.contains(range.commonAncestorContainer)) {
                updatedQuestions[currentQuestionIndex] = {
                    ...currentQ,
                    text: questionTextRef.current.innerHTML
                };
            }
            setFlatQuestions(updatedQuestions);
        } catch (e) {
            console.warn("Highlight failed via surroundContents. Text might cross blocks.");
            showToast("Warning", "Selection crosses multiple paragraphs. Select within one block.", "error");
        }
    }, [flatQuestions, currentQuestionIndex, showToast]);

    // --- Text Selection Handler ---
    const handleTextSelection = useCallback((e) => {
        if (isMouseDown.current) return;

        setTimeout(() => {
            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
                setSelectionToolbar(null);
                return;
            }

            const range = selection.getRangeAt(0);
            const text = selection.toString().trim();
            
            if (!text) {
                setSelectionToolbar(null);
                return;
            }

            const isInsideScenario = scenarioRef.current && scenarioRef.current.contains(range.commonAncestorContainer);
            const isInsideQuestion = questionTextRef.current && questionTextRef.current.contains(range.commonAncestorContainer);

            if (!isInsideScenario && !isInsideQuestion) {
                setSelectionToolbar(null);
                return;
            }

            if (isHighlighterActive) {
                applyHighlight(range, highlightColor);
                selection.removeAllRanges();
                setSelectionToolbar(null);
            } else {
                const rect = range.getBoundingClientRect();
                setSelectionToolbar({
                    top: rect.top + window.scrollY - 55, 
                    left: rect.left + (rect.width / 2),
                    text: text,
                    range: range.cloneRange() 
                });
            }
        }, 100);
    }, [isHighlighterActive, highlightColor, applyHighlight]);

    const handleCopySelection = useCallback(async () => {
        if (selectionToolbar?.text) {
            try {
                await navigator.clipboard.writeText(selectionToolbar.text);
                setSelectionToolbar(null);
                showToast("Copied", "Text copied to clipboard!", "success");
            } catch (err) {
                console.error("Failed to copy", err);
            }
        }
    }, [selectionToolbar, showToast]);

    const handleManualHighlight = useCallback(() => {
        if (selectionToolbar?.range) {
            applyHighlight(selectionToolbar.range, '#fef08a');
            setSelectionToolbar(null);
            window.getSelection().removeAllRanges();
        }
    }, [selectionToolbar, applyHighlight]);

    // --- Resizable Split Screen ---
    useEffect(() => {
        const mainPanel = mainPanelRef.current;
        const divider = dividerRef.current;
        if (!mainPanel || !divider) return;

        const resize = (e) => {
            const rect = mainPanel.getBoundingClientRect();
            const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
            if (newWidth > 20 && newWidth < 80) {
                setQuestionPanelWidth(newWidth);
            }
        };

        const stopResizing = () => {
            document.body.style.cursor = 'default';
            document.body.style.userSelect = 'auto';
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
        };

        const startResizing = (e) => {
            e.preventDefault();
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            window.addEventListener('mousemove', resize);
            window.addEventListener('mouseup', stopResizing);
        };

        divider.addEventListener('mousedown', startResizing);

        return () => {
            divider.removeEventListener('mousedown', startResizing);
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
        };
    }, []);

    // --- Navigation Handlers ---
    const handleNext = () => {
        if(currentQuestionIndex < flatQuestions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if(currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };
    
    const getCompletionPercentage = () => {
        const total = flatQuestions.length;
        if(total === 0) return 0;
        const answeredCount = flatQuestions.filter(q=>q.type==='descriptive'?hasDescriptiveResponse(q,answers[q.id]):answers[q.id]!==undefined && answers[q.id]!==null).length;
        return Math.round((answeredCount / total) * 100);
    };

    // --- MAINTENANCE VIEW ---
    if (isMaintenance) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-center px-4 animate-fade-in">
                <div className="bg-white p-8 rounded-2xl shadow-xl max-w-lg w-full border border-purple-100">
                    <div className="text-6xl mb-6">🛠️</div>
                    <h1 className="text-3xl font-extrabold text-gray-800 mb-4">Under Maintenance</h1>
                    <p className="text-gray-600 mb-6 text-lg">
                        The exam platform is currently undergoing critical updates. 
                        Please wait until maintenance is complete to start or resume your exam.
                    </p>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                        <div className="bg-[#D02C70] h-2 rounded-full animate-pulse w-2/3"></div>
                    </div>
                    <p className="text-sm text-gray-400">Your progress (if any) is safe.</p>
                </div>
            </div>
        );
    }

    // --- ACCESS ERROR VIEW (Unpublished / Hidden) ---
    if (accessError) {
        const errorConfig = {
            'sealed-elsewhere': { icon: '↻', title: 'Submission started in another window', desc: 'Reopen this exam to finish with the answers already saved for submission.' },
            'hidden': { icon: '🔒', title: 'Exam Unavailable', desc: 'This exam has been hidden by the instructor and is currently unavailable.' },
            'unpublished': { icon: '🚧', title: 'Exam Not Published', desc: 'This exam is currently a draft and has not been published yet. Please wait for the instructor to publish it.' }
        };
        const config = errorConfig[accessError] || { icon: '↻', title: 'Exam could not be opened', desc: 'Check your connection and reopen this exam. If the problem continues, contact your coordinator. Your saved attempt has not been submitted.' };

        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-center px-4 animate-fade-in">
                <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-gray-200">
                    <div className="text-6xl mb-6">{config.icon}</div>
                    <h1 className="text-2xl font-bold text-gray-800 mb-3">{config.title}</h1>
                    <p className="text-gray-600 mb-6">{config.desc}</p>
                    <button 
                        type="button"
                        onClick={() => navigate('/dashboard')}
                        className="bg-gray-800 text-white font-bold py-2.5 px-6 rounded-lg hover:bg-gray-900 transition-colors w-full"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    // --- ATTEMPT BLOCKED VIEW ---
    if (isAttemptBlocked) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-center px-4 animate-fade-in">
                <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-red-100">
                    <div className="text-6xl mb-6">🚫</div>
                    <h1 className="text-2xl font-bold text-gray-800 mb-3">Attempt Limit Reached</h1>
                    <p className="text-gray-600 mb-6">
                        You have already submitted this exam. Access to retake is currently restricted.
                    </p>
                    <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm mb-6 border border-red-200">
                        Please contact the administrator if you believe this is an error or need a re-attempt.
                    </div>
                    <button 
                        type="button"
                        onClick={() => navigate('/dashboard')}
                        className="bg-gray-800 text-white font-bold py-2.5 px-6 rounded-lg hover:bg-gray-900 transition-colors w-full"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    // --- RENDER ---
    if (loadingExam || !rawExam || !isLoaded) {
        return (
            <div className="min-h-screen flex items-center justify-center p-8">
                <Skeleton type="question" className="w-full max-w-4xl" />
            </div>
        );
    }
    if (isSubmitting || submissionNotice || attemptFinished.current) {
        const failed = submissionNotice === 'failed';
        const title = isSubmitting ? 'Submitting your exam' : attemptFinished.current ? 'Exam submitted' : failed ? 'Submission failed' : submissionNotice === 'expired' ? 'Your saved exam time has ended' : 'Finish your saved submission';
        return (
            <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-orange-50 flex items-center justify-center p-6">
                <div className="w-full max-w-lg rounded-2xl bg-white border border-pink-100 shadow-xl p-8 text-center" role="status" aria-live="polite">
                    {isSubmitting && <Spinner color="border-[#D02C70]" />}
                    <h1 className="text-2xl font-bold text-gray-800 mt-4 mb-3">{title}</h1>
                    <p className="text-gray-600 mb-4">{isSubmitting ? submissionProgress : attemptFinished.current ? 'Your answers have been saved.' : failed ? 'Your answers are kept on this page. Check your connection, then retry this same submission.' : 'Your saved answers are ready to submit. Opening this page has not submitted them again.'}</p>
                    {isSubmitting ? <p className="text-sm text-gray-500">Please keep this window open. You will see a result or a retry option if the connection fails.</p> : !attemptFinished.current && <button type="button" onClick={handleSubmit} className="w-full rounded-xl bg-gradient-to-r from-[#D02C70] to-[#F05530] py-3 font-bold text-white">{failed ? 'Retry submission' : 'Finish submission'}</button>}
                    {!isSubmitting && <button type="button" onClick={() => navigate('/dashboard')} className="mt-4 text-sm font-semibold text-gray-600">Back to dashboard</button>}
                </div>
            </div>
        );
    }

    const currentQuestion = flatQuestions[currentQuestionIndex];
    if (!currentQuestion) {
        return (
             <div className="fixed inset-0 bg-white flex flex-col items-center justify-center">
                <p className="text-red-500 text-lg">Error: Question data is missing or corrupted.</p>
                <button type="button" onClick={() => navigate('/dashboard')} className="mt-4 bg-gray-200 px-4 py-2 rounded">Return to Dashboard</button>
             </div>
        );
    }

    const currentAnswer = getCurrentAnswer();
    const currentView = (typeof currentAnswer === 'object' && currentAnswer?.view) ? currentAnswer.view : hasStarterSpreadsheet(currentQuestion)?'excel':'word'; 
    
    const isFlagged = flaggedQuestions[currentQuestion.id];

    const colors = [
        { name: 'Yellow', code: '#fef08a' },
        { name: 'Green', code: '#bbf7d0' },
        { name: 'Cyan', code: '#a5f3fc' },
        { name: 'Pink', code: '#fbcfe8' }
    ];

    return (
        <div className="fixed inset-0 bg-[#FFF5F9] flex flex-col font-sans">
            <style>{styles}</style>
            
            {showCalculator && <CalculatorModal onClose={() => setShowCalculator(false)} />}
            {showFormulaSheet && paperData && <FormulaSheetModal paper={paperData} onClose={() => setShowFormulaSheet(false)} />}
            
            <ToastContainer toasts={toasts} removeToast={removeToast} />

            {/* FLOATING SELECTION TOOLBAR */}
            {selectionToolbar && !isHighlighterActive && (
                <div 
                    className="fixed z-[999] bg-gray-900 text-white rounded shadow-lg flex items-center gap-1 px-2 py-1 transform -translate-x-1/2 transition-opacity animate-slide-up"
                    style={{ top: selectionToolbar.top, left: selectionToolbar.left }}
                    onMouseDown={(e) => e.preventDefault()} 
                >
                    <button 
                        type="button"
                        onClick={handleCopySelection}
                        className="text-xs font-bold px-3 py-1 hover:bg-gray-700 rounded transition-colors flex items-center gap-1"
                    >
                        <span>📋</span> Copy
                    </button>
                    <div className="w-px h-4 bg-gray-600"></div>
                    <button 
                        type="button"
                        onClick={handleManualHighlight}
                        className="text-xs font-bold px-3 py-1 hover:bg-gray-700 rounded transition-colors flex items-center gap-1"
                    >
                        <span className="w-3 h-3 bg-yellow-300 rounded-full inline-block"></span> Highlight
                    </button>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-0.5 border-4 border-transparent border-t-gray-900"></div>
                </div>
            )}

            {/* --- TOP PROGRESS BAR --- */}
            <div className="h-1.5 w-full bg-gray-100 relative">
                <div 
                    className="h-full bg-gradient-to-r from-[#D02C70] to-[#F05530] transition-all duration-500 ease-out shadow-[0_0_10px_rgba(208,44,112,0.5)]"
                    style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
            </div>

            {/* Header */}
            <header className="flex-shrink-0 bg-white border-b px-4 py-2 flex justify-between items-center shadow-sm z-20">
                <div className="flex items-center gap-3">
                    <img src="https://firebasestorage.googleapis.com/v0/b/exam-platform-v2.firebasestorage.app/o/public%2FInstill%20logo%20copy.png?alt=media&token=811c3b74-7d3d-4988-9310-dbc0e8267304" alt="Logo" className="w-9 h-9" />
                    <div>
                        <h1 className="text-xl font-extrabold tracking-tight text-instil-gradient">Instil Learning Hub</h1>
                        <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide truncate max-w-[200px]">{rawExam.title}</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* Highlighter */}
                    <div className="hidden md:flex items-center bg-gray-50 border rounded-lg overflow-hidden">
                         <button
                            type="button"
                            onClick={() => setIsHighlighterActive(!isHighlighterActive)}
                            className={`flex items-center gap-2 text-sm font-bold py-1.5 px-3 transition-all btn-press ${
                                isHighlighterActive
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                            title="Toggle Highlighter"
                        >
                            <span>🖍️</span> 
                        </button>
                        {isHighlighterActive && (
                            <div className="flex px-2 gap-1 border-l border-gray-200">
                                {colors.map(c => (
                                    <button
                                        type="button"
                                        key={c.code}
                                        onClick={() => setHighlightColor(c.code)}
                                        className={`w-4 h-4 rounded-full border border-gray-300 transition-transform ${highlightColor === c.code ? 'scale-125 ring-2 ring-gray-400' : 'hover:scale-110'}`}
                                        style={{ backgroundColor: c.code }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="h-6 w-px bg-gray-200 hidden md:block"></div>

                    <button
                        type="button"
                        onClick={toggleFlagQuestion}
                        className={`text-sm font-bold py-1.5 px-3 rounded-lg border transition-all btn-press ${
                            isFlagged
                                ? 'bg-orange-50 text-[#F05530] border-[#F05530]/30'
                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        {isFlagged ? '🚩 Flagged' : '🏳️ Flag'}
                    </button>
                    
                    <button type="button" onClick={() => setShowCalculator(true)} className="hidden md:block bg-white text-gray-600 border border-gray-200 font-bold text-sm py-1.5 px-3 rounded-lg hover:bg-gray-50 btn-press">🧮 Calc</button>
                    <button type="button" onClick={() => setShowFormulaSheet(true)} disabled={!paperData} className="hidden md:block bg-white text-gray-600 border border-gray-200 font-bold text-sm py-1.5 px-3 rounded-lg hover:bg-gray-50 disabled:opacity-50 btn-press">📑 Formulas</button>
                    
                    {/* ISOLATED TIMER COMPONENT HERE */}
                    <span className="hidden sm:inline text-xs text-gray-500" aria-live="polite">{isSaving ? 'Saving…' : hasUnsavedChanges ? 'Changes not yet saved' : 'Saved'}</span>
                    <ExamTimer targetEndTime={targetEndTime} onTimeUp={handleTimeUp} />

                    <button 
                        type="button"
                        onClick={handleSubmit} 
                        className="bg-gradient-to-r from-[#D02C70] to-[#F05530] text-white font-bold py-2 px-6 rounded-lg hover:shadow-lg hover:opacity-90 transition-all btn-press"
                    >
                        Submit
                    </button>
                </div>
            </header>

            {attendance.session && (attendance.session.phase==='sealed'||attendance.connectionError) && <div className="attendance-exam-banner" role="status">{attendance.session.phase==='sealed' && <p>{attendance.resumeMessage}</p>}{attendance.connectionError && <p>{attendance.connectionError}</p>}</div>}
            <main ref={mainPanelRef} className="flex-grow flex overflow-hidden relative">
                {/* --- LEFT PANEL: QUESTION / SCENARIO --- */}
                <div 
                    style={{ width: `${questionPanelWidth}%`, userSelect: 'text', WebkitUserSelect: 'text', MozUserSelect: 'text', msUserSelect: 'text' }} 
                    className={`p-6 overflow-y-auto bg-gray-50/50 flex flex-col relative border-r select-text ${isHighlighterActive ? 'cursor-text' : ''}`}
                    onMouseDown={() => { isMouseDown.current = true; }}
                    onMouseUp={(e) => { isMouseDown.current = false; handleTextSelection(e); }}
                >
                    {/* Use Memoized Question Display */}
                    <QuestionDisplay 
                        question={currentQuestion}
                        index={currentQuestionIndex}
                        sectionInfo={currentSectionInfo}
                        isFirstOfSection={isFirstQuestionOfSection}
                        view={currentView}
                        answer={currentAnswer}
                        onDescriptiveChange={handleDescriptiveAnswerChange}
                        onViewChange={setDescriptiveAnswerView}
                        scenarioRef={scenarioRef}
                        questionTextRef={questionTextRef}
                    />
                </div>
                
                {/* Resizer */}
                <div ref={dividerRef} className="cursor-col-resize w-1.5 bg-gray-200 hover:bg-[#D02C70] transition-colors flex-shrink-0 z-10 shadow-sm"></div>

                {/* --- RIGHT PANEL: ANSWER INPUT --- */}
                <div style={{ width: `${100 - questionPanelWidth}%` }} className="p-6 overflow-y-auto flex flex-col bg-white">
                    <div key={`ans-${currentQuestion.id}`} className="flex-grow flex flex-col animate-slide-up h-full">
                        <h2 className="text-lg font-bold mb-4 text-gray-400 uppercase tracking-wider border-b pb-2 flex-shrink-0">Your Answer</h2>
                        
                        {currentQuestion.type === 'mcq' && (
                            <div className="space-y-3">
                                <p className="text-xs font-bold text-[#D02C70] bg-[#FFF0F5] p-2 rounded border border-[#D02C70]/20 inline-block mb-2">
                                    Select all that apply
                                </p>
                                {currentQuestion.options.map((option, oIndex) => (
                                    <label key={oIndex} className={`block p-4 border rounded-xl cursor-pointer transition-all duration-200 group ${ (currentAnswer || []).includes(oIndex) ? 'bg-[#FFF0F5] border-[#D02C70] shadow-md transform scale-[1.01]' : 'hover:bg-gray-50 border-gray-200' }`}>
                                            <div className="flex items-start gap-3">
                                                <div className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${ (currentAnswer || []).includes(oIndex) ? 'bg-[#D02C70] border-[#D02C70]' : 'border-gray-300 group-hover:border-[#D02C70]' }`}>
                                                    {(currentAnswer || []).includes(oIndex) && <span className="text-white text-xs">✓</span>}
                                                </div>
                                                <span className={`text-sm ${ (currentAnswer || []).includes(oIndex) ? 'font-semibold text-[#D02C70]' : 'text-gray-700' }`}>{option}</span>
                                           </div>
                                           <input type="checkbox" checked={(currentAnswer || []).includes(oIndex)} onChange={() => handleMcqAnswerChange(oIndex)} className="hidden" />
                                   </label>
                                ))}
                            </div>
                        )}
                        
                        {currentQuestion.type === 'short-answer' && (
                             <div className="relative">
                                <input
                                    type="text"
                                    value={currentAnswer || ''}
                                    onChange={(e) => handleShortAnswerChange(e.target.value)}
                                    className="w-full p-4 border-2 border-gray-200 rounded-xl text-lg focus:ring-4 focus:ring-[#D02C70]/20 focus:border-[#D02C70] outline-none transition-all"
                                    placeholder="Type your answer here..."
                                />
                                <div className="absolute right-3 top-4 text-gray-400 text-sm">↵</div>
                             </div>
                        )}

                        {currentQuestion.type === 'dropdown' && (
                            <div className="space-y-2">
                                <label className="font-semibold text-gray-700 text-sm">Select Option:</label>
                                <div className="relative">
                                    <select 
                                        value={currentAnswer !== undefined ? currentAnswer : ''} 
                                        onChange={(e) => updateAnswer(Number(e.target.value))}
                                        className="w-full p-3 border-2 border-gray-200 rounded-xl bg-white appearance-none cursor-pointer focus:border-[#D02C70] focus:ring-4 focus:ring-[#D02C70]/20 outline-none transition-all"
                                    >
                                        <option value="" disabled>-- Choose an option --</option>
                                        {currentQuestion.options.map((opt, idx) => (
                                            <option key={idx} value={idx}>{opt}</option>
                                        ))}
                                    </select>
                                    <div className="absolute right-4 top-3.5 pointer-events-none text-gray-500">▼</div>
                                </div>
                            </div>
                        )}

                        {currentQuestion.type === 'matching' && (() => {
                            const studentMatches = currentAnswer || {};
                            const matchedOptionIndexes = Object.values(studentMatches);
                            const unmatchedOptions = currentQuestion.options.map((opt, index) => ({ opt, index })).filter(item => !matchedOptionIndexes.includes(item.index));
                            return (
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <h3 className="font-bold text-center text-gray-500 text-sm uppercase">Prompts</h3>
                                        {currentQuestion.prompts.map((prompt, pIndex) => (
                                            <div 
                                                key={pIndex} 
                                                className="p-3 border-2 border-dashed border-gray-300 rounded-lg flex flex-col justify-center gap-2 min-h-[80px] bg-gray-50 transition-colors hover:bg-gray-100" 
                                                onDragOver={(e) => e.preventDefault()} 
                                                onDrop={(e) => { 
                                                    e.preventDefault(); 
                                                    if(draggedItem.current !== null) {
                                                        const optionIndex = parseInt(draggedItem.current, 10); 
                                                        handleMatchingDrop(pIndex, optionIndex);
                                                        draggedItem.current = null;
                                                    }
                                                }}
                                            >
                                                <span className="font-semibold text-gray-800 text-sm text-center">{prompt}</span>
                                                {studentMatches[pIndex] !== undefined && (
                                                    <div 
                                                        className="p-2 bg-[#FFF0F5] text-[#D02C70] border border-[#D02C70]/30 rounded-md text-xs font-bold cursor-grab active:cursor-grabbing text-center shadow-sm w-full" 
                                                        draggable 
                                                        onDragStart={() => draggedItem.current = studentMatches[pIndex]}
                                                    >
                                                        {currentQuestion.options[studentMatches[pIndex]]}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <div 
                                        className="space-y-3 p-3 border rounded-xl bg-gray-50 shadow-inner" 
                                        onDragOver={e => e.preventDefault()} 
                                        onDrop={e => {
                                            e.preventDefault();
                                            if(draggedItem.current !== null) {
                                                const optionIndexToUnmatch = parseInt(draggedItem.current, 10);
                                                const next = { ...(answersRef.current[currentQuestion.id] || {}) };
                                                Object.keys(next).forEach(key => { if (next[key] === optionIndexToUnmatch) delete next[key]; });
                                                updateAnswer(next);
                                                draggedItem.current = null;
                                            }
                                        }}
                                    >
                                        <h3 className="font-bold text-center text-gray-500 text-sm uppercase">Available Options</h3>
                                        <p className="text-[10px] text-center text-gray-400 mb-2">Drag to match</p>
                                        <div className="flex flex-col gap-2">
                                            {unmatchedOptions.map(({ opt, index }) => (
                                                <div 
                                                    key={index} 
                                                    draggable 
                                                    onDragStart={() => draggedItem.current = index} 
                                                    className="p-3 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 cursor-grab active:cursor-grabbing text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                                                >
                                                    {opt}
                                                </div>
                                            ))}
                                        </div>
                                        {unmatchedOptions.length === 0 && <p className="text-sm text-center text-green-500 font-bold py-4">All matched! 🎉</p>}
                                    </div>
                                </div>
                            )
                        })()}

                        {currentQuestion.type === 'mcq-grid' && ( 
                            <div className="overflow-x-auto border rounded-xl shadow-sm">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="p-3"></th>
                                            {currentQuestion.columns.map((col, idx) => (
                                                <th key={idx} className="p-3 font-bold text-gray-600 text-center">{col}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white">
                                        {currentQuestion.rows.map((row, rIdx) => (
                                            <tr key={rIdx} className="border-t hover:bg-gray-50 transition-colors">
                                                <td className="p-3 font-semibold text-gray-800">{row}</td>
                                                {currentQuestion.columns.map((_, cIdx) => (
                                                    <td key={cIdx} className="p-3 text-center">
                                                        <input 
                                                            type="radio" 
                                                            name={`grid-${currentQuestion.id}-${rIdx}`} 
                                                            checked={(currentAnswer || {})[rIdx] === cIdx}
                                                            onChange={() => updateAnswer({ ...(currentAnswer || {}), [rIdx]: cIdx })}
                                                            className="w-5 h-5 text-[#D02C70] focus:ring-[#D02C70] cursor-pointer"
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div> 
                        )}

                        {currentQuestion.type === 'descriptive' && (
                            <div className="flex-grow flex flex-col h-full overflow-hidden">
                               {currentView === 'word' && (
                                   <div className="flex-grow border rounded-xl shadow-sm overflow-hidden bg-white h-full">
                                           <AdvancedWordProcessor 
                                               key={`${attemptId}:${currentQuestion.id}`} 
                                               value={typeof currentAnswer === 'object' ? currentAnswer?.wordContent || '' : currentAnswer || ''} 
                                               onChange={(content) => handleDescriptiveAnswerChange('wordContent', content)} 
                                           />
                                   </div>
                               )}
                               
                               {currentView === 'excel' && (
                                   <div className="flex-grow border rounded-xl shadow-sm overflow-hidden bg-white h-full">
                                           <ExamSpreadsheetResponse
                                               key={`${attemptId}:${currentQuestion.id}`}
                                               question={currentQuestion}
                                               answer={currentAnswer}
                                               onChange={data=>handleDescriptiveAnswerChange('excelData',data)}
                                           />
                                   </div>
                               )}
                            </div>
                        )}
                    </div>
                </div>
            </main>

            <footer className="flex-shrink-0 bg-white border-t p-3 z-20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                <div className="flex flex-col gap-4 max-w-6xl mx-auto">
                    <div className="flex justify-between items-center">
                        <button 
                            type="button"
                            onClick={handlePrev} 
                            disabled={currentQuestionIndex === 0} 
                            className="flex items-center gap-2 text-gray-600 font-bold py-2 px-6 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            ← Previous
                        </button>

                        <button 
                            type="button"
                            onClick={() => setShowNavigator(!showNavigator)} 
                            className={`flex items-center gap-2 font-bold py-2 px-6 rounded-full transition-all ${showNavigator ? 'bg-[#FFF0F5] text-[#D02C70]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >
                            <span></span> {showNavigator ? 'Hide Navigator' : 'Show Navigator'}
                        </button>

                        <button 
                            type="button"
                            onClick={handleNext} 
                            disabled={currentQuestionIndex >= flatQuestions.length - 1} 
                            className="flex items-center gap-2 bg-gradient-to-r from-[#D02C70] to-[#F05530] text-white font-bold py-2 px-6 rounded-lg hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed shadow-md transition-all btn-press"
                        >
                            Next →
                        </button>
                    </div>

                    {showNavigator && (
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 animate-slide-up">
                            <QuestionNavigator
                                questions={flatQuestions}
                                currentIndex={currentQuestionIndex}
                                setCurrentIndex={setCurrentQuestionIndex}
                                answers={answers}
                                flaggedQuestions={flaggedQuestions}
                            />
                        </div>
                    )}
                </div>
            </footer>
        </div>
    );
};

// --- Sub-Components ---

const QuestionNavigator = ({ questions, currentIndex, setCurrentIndex, answers, flaggedQuestions }) => {
    const groupedQuestions = questions.reduce((acc, q, index) => {
        const sectionName = q.section || 'General';
        if (!acc[sectionName]) acc[sectionName] = [];
        acc[sectionName].push({ ...q, originalIndex: index });
        return acc;
    }, {});

    return (
        <div className="max-h-[40vh] overflow-y-auto w-full pr-2 space-y-6 custom-scrollbar">
            {Object.entries(groupedQuestions).map(([section, qs]) => (
                <div key={section} className="flex flex-col">
                    <h4 className="sticky top-0 bg-gray-50 z-10 text-xs font-extrabold text-[#D02C70] py-2 border-b border-gray-200 mb-3 uppercase tracking-wider">
                        {section}
                    </h4>
                    <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-2">
                        {qs.map((q) => {
                            const index = q.originalIndex;
                            const answer = answers[q.id];
                            let isAnswered = false;
                            
                            if (answer) {
                                if (q.type === 'descriptive') isAnswered = hasDescriptiveResponse(q,answer);
                                else if (q.type === 'mcq-grid') isAnswered = Object.keys(answer).length === q.rows.length;
                                else if (q.type === 'matching') isAnswered = Object.keys(answer).length > 0;
                                else if (q.type === 'short-answer') isAnswered = !!answer;
                                else if (q.type === 'dropdown') isAnswered = answer !== undefined;
                                else isAnswered = Array.isArray(answer) ? answer.length > 0 : (answer !== undefined && answer !== null);
                            }

                            const isFlagged = flaggedQuestions[q.id] === true;

                            return (
                                <button
                                    type="button"
                                    key={q.id}
                                    onClick={() => setCurrentIndex(index)}
                                    className={`w-full aspect-square rounded-md flex items-center justify-center font-bold text-sm relative transition-all duration-200 transform hover:scale-105
                                        ${currentIndex === index ? 'bg-gradient-to-r from-[#D02C70] to-[#F05530] text-white shadow-md scale-110 z-10' : 
                                         isFlagged ? 'bg-yellow-400 text-yellow-900 shadow-md' : 
                                         isAnswered ? 'bg-[#D02C70]/80 text-white' : 
                                         'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'}`}
                                    title={isFlagged ? `Question ${index + 1} (Flagged)` : `Question ${index + 1}`}
                                >
                                    {index + 1}
                                    {isFlagged && <span className="absolute -top-1.5 -right-1.5 text-xs drop-shadow-sm">🚩</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};

const CalculatorModal = ({ onClose }) => {
    const [input, setInput] = useState('');
    const [result, setResult] = useState('');

    const handleInput = (value) => {
        if (result) {
            setInput(result + value);
            setResult('');
        } else {
            setInput(input + value);
        }
    };

    const calculateResult = () => {
        try {
            if (/[^0-9+\-*/.% ]/.test(input)) {
                throw new Error("Invalid Characters");
            }
            // eslint-disable-next-line no-new-func
            const calculatedResult = new Function('return (' + input + ')')();
            setResult(calculatedResult.toString());
        } catch (error) {
            setResult('Error');
        }
    };

    const clearInput = () => {
        setInput('');
        setResult('');
    };

    const backspace = () => {
        if (result) {
            clearInput();
        } else {
            setInput(input.slice(0, -1));
        }
    };

    const handleButtonClick = (btn) => {
        if (btn === '=') {
            calculateResult();
        } else if (['/', '*', '-', '+', '.'].includes(btn)) {
            handleInput(btn);
        } else { 
            handleInput(btn);
        }
    };

    const buttonGrid = [
        ['C', '<-', '%', '/'],
        ['7', '8', '9', '*'],
        ['4', '5', '6', '-'],
        ['1', '2', '3', '+'],
        ['0', '.', '=']
    ];

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 animate-slide-up">
            <div className="bg-white p-5 rounded-2xl shadow-2xl w-full max-w-xs border border-gray-100">
                 <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">🧮 Calculator</h3>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-800 transition-colors text-2xl">&times;</button>
                 </div>
                <div className="bg-gray-100 p-4 rounded-xl text-right text-3xl font-mono mb-4 break-all text-gray-800 tracking-wider shadow-inner h-20 flex items-center justify-end overflow-hidden">
                    {result || input || '0'}
                </div>
                <div className="grid grid-cols-4 gap-2">
                    <button type="button" onClick={clearInput} className="bg-red-100 text-red-600 font-bold p-3 rounded-lg hover:bg-red-200 transition btn-press">AC</button>
                    <button type="button" onClick={backspace} className="bg-gray-200 text-gray-700 font-bold p-3 rounded-lg hover:bg-gray-300 transition btn-press">⌫</button>
                    <button type="button" onClick={() => handleInput('%')} className="bg-gray-200 text-gray-700 font-bold p-3 rounded-lg hover:bg-gray-300 transition btn-press">%</button>
                    <button type="button" onClick={() => handleInput('/')} className="bg-[#D02C70]/10 text-[#D02C70] font-bold p-3 rounded-lg hover:bg-[#D02C70]/20 transition btn-press">÷</button>
                    {['7','8','9','*','4','5','6','-','1','2','3','+'].map(btn => (
                        <button
                            type="button"
                            key={btn}
                            onClick={() => handleInput(btn)}
                            className={`p-3 rounded-lg font-bold text-lg transition shadow-sm btn-press ${['*','-','+'].includes(btn) ? 'bg-[#D02C70]/10 text-[#D02C70] hover:bg-[#D02C70]/20' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
                        >
                            {btn === '*' ? '×' : btn}
                        </button>
                    ))}
                    <button type="button" onClick={() => handleInput('0')} className="col-span-2 bg-white text-gray-700 border border-gray-200 font-bold p-3 rounded-lg hover:bg-gray-50 transition btn-press">0</button>
                    <button type="button" onClick={() => handleInput('.')} className="bg-white text-gray-700 border border-gray-200 font-bold p-3 rounded-lg hover:bg-gray-50 transition btn-press">.</button>
                    <button type="button" onClick={calculateResult} className="bg-[#D02C70] text-white font-bold p-3 rounded-lg hover:opacity-90 transition btn-press">=</button>
                </div>
            </div>
        </div>
    );
};

const FormulaSheetModal = ({ paper, onClose }) => {
    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-slide-up">
            <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
                <div className="flex justify-between items-center mb-4 flex-shrink-0 border-b pb-4">
                    <h3 className="text-xl font-bold text-gray-800">📑 Formula Sheet: {paper.name}</h3>
                    <button type="button" onClick={onClose} className="text-3xl text-gray-400 hover:text-gray-800">&times;</button>
                </div>
                <div className="overflow-y-auto space-y-4 pr-2 flex-1">
                    {!paper.formulaSheetType || paper.formulaSheetType === 'none' ? (
                        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                            <span className="text-4xl mb-2">🚫</span>
                            <p>No formula sheet provided for this exam.</p>
                        </div>
                    ) : paper.formulaSheetType === 'text' ? (
                        <pre className="whitespace-pre-wrap font-mono text-sm bg-gray-50 p-4 rounded-lg border">{paper.formulaSheetContent}</pre>
                    ) : (
                        Array.isArray(paper.formulaSheetContent) && paper.formulaSheetContent.map((url, index) => (
                            <img key={index} src={url} alt={`Formula Sheet Page ${index + 1}`} className="w-full h-auto border rounded-lg shadow-sm" />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};
export default ExamTaker;